import { useEffect, useState, useCallback } from 'react';
import {
  websocketService,
  BlockchainEvent,
  VoteRecordedEvent,
  ElectionCreatedEvent,
  ElectionStatusChangedEvent,
  TallyingEvent,
  EventCallback,
} from '../services/websocket.service';

export interface UseWebSocketOptions {
  autoConnect?: boolean;
  subscribeToElection?: string;
  subscribeToAdmin?: boolean;
}

export interface WebSocketState {
  connected: boolean;
  reconnectAttempts: number;
}

/**
 * Custom hook for managing WebSocket connections and event subscriptions
 */
export function useWebSocket(token: string | null, options: UseWebSocketOptions = {}) {
  const { autoConnect = true, subscribeToElection, subscribeToAdmin = false } = options;
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    reconnectAttempts: 0,
  });

  // Connect to WebSocket when token is available
  useEffect(() => {
    if (!token || !autoConnect) {
      return;
    }

    websocketService.connect(token);

    // Setup connection status listeners
    const handleConnected = () => {
      setState(websocketService.getConnectionStatus());
    };

    const handleDisconnected = () => {
      setState(websocketService.getConnectionStatus());
    };

    websocketService.on('connection:established', handleConnected);
    websocketService.on('connection:lost', handleDisconnected);

    // Subscribe to election or admin channel when connected
    const handleConnection = () => {
      if (subscribeToElection) {
        websocketService.subscribeToElection(subscribeToElection);
      }
      if (subscribeToAdmin) {
        websocketService.subscribeToAdminChannel();
      }
    };

    websocketService.on('connection:established', handleConnection);

    // Cleanup on unmount
    return () => {
      websocketService.off('connection:established', handleConnected);
      websocketService.off('connection:lost', handleDisconnected);
      websocketService.off('connection:established', handleConnection);

      if (subscribeToElection) {
        websocketService.unsubscribeFromElection(subscribeToElection);
      }
    };
  }, [token, autoConnect, subscribeToElection, subscribeToAdmin]);

  // Manual connect/disconnect
  const connect = useCallback(() => {
    if (token) {
      websocketService.connect(token);
    }
  }, [token]);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
    setState({ connected: false, reconnectAttempts: 0 });
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    subscribe: (electionId: string) => websocketService.subscribeToElection(electionId),
    unsubscribe: (electionId: string) => websocketService.unsubscribeFromElection(electionId),
    subscribeToAdmin: () => websocketService.subscribeToAdminChannel(),
  };
}

/**
 * Hook for listening to specific blockchain events
 */
export function useBlockchainEvent<T = any>(
  event: BlockchainEvent | string,
  callback: EventCallback<T>,
  dependencies: any[] = []
) {
  useEffect(() => {
    websocketService.on(event, callback);

    return () => {
      websocketService.off(event, callback);
    };
  }, [event, ...dependencies]);
}

/**
 * Hook for vote recorded events
 */
export function useVoteRecorded(callback: EventCallback<VoteRecordedEvent>) {
  useBlockchainEvent('vote:recorded', callback, [callback]);
}

/**
 * Hook for election created events
 */
export function useElectionCreated(callback: EventCallback<ElectionCreatedEvent>) {
  useBlockchainEvent('election:created', callback, [callback]);
}

/**
 * Hook for election status changed events
 */
export function useElectionStatusChanged(callback: EventCallback<ElectionStatusChangedEvent>) {
  useBlockchainEvent('election:status_changed', callback, [callback]);
}

/**
 * Hook for tallying started events
 */
export function useTallyingStarted(callback: EventCallback<TallyingEvent>) {
  useBlockchainEvent('tallying:started', callback, [callback]);
}

/**
 * Hook for tallying completed events
 */
export function useTallyingCompleted(callback: EventCallback<TallyingEvent>) {
  useBlockchainEvent('tallying:completed', callback, [callback]);
}

/**
 * Hook for tracking live vote count for an election
 */
export function useLiveVoteCount(electionId: string) {
  const [voteCount, setVoteCount] = useState(0);

  useVoteRecorded((data) => {
    if (data.electionId === electionId) {
      setVoteCount((prev) => prev + 1);
    }
  });

  return voteCount;
}
